import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostBinding, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { FoodHeader, FoodOrigin, getFoodOrigin } from '@cf-shared/data-model/food';
import { UiDisplayNutrient, uiDisplayNutrients } from 'models/display-nutrient';
import { ImageService } from 'services/image-service';
import { SettingsService } from 'services/settings.service';
import { formatServingSize } from 'util/formatting';
import { CsCardInterfaceClass, CsCardComponent } from 'components/cs-card/cs-card';
import { DestroyGuard } from 'services/browser-utils';

import { LL } from 'i18n/i18n.service';
import { hasClientSmallScreen } from 'environments/environment-helpers';
import { TranslatedString } from 'models/translated-string';
export enum LikeState {
  None = 'none',
  Liked = 'liked',
  Disliked = 'disliked'
}

@Component({
  selector: 'cs-food-card',
  templateUrl: 'cs-food-card.html',
  host: { 'class': 'food-card' },
  providers: [{ provide: CsCardInterfaceClass, useExisting: CsFoodCardComponent }],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CsFoodCardComponent extends CsCardInterfaceClass implements AfterViewInit, OnDestroy {
  protected LL = LL;
  protected hasClientSmallScreen = hasClientSmallScreen;
  private destroyGuard = new DestroyGuard();
  @ViewChild(CsCardComponent) card: CsCardComponent;
  @Input() food: FoodHeader;
  @Input() description?: TranslatedString;
  @Input() imageUrl: string;
  /**
   * A staple food's own picture, shown in the image slot when the food has no photo of its own
   * (staple pictures plan, #5694). Drawn as a rounded SQUARE, so it never reads as a photo the user
   * took — those stay round. Setting it again clears a previous load failure, because the new url is
   * a different file and deserves its own try.
   */
  @Input() set staplePictureUrl(url: string | undefined) {
    this.staplePictureUrlValue = url;
    this.staplePictureFailed = false;
  }
  get staplePictureUrl(): string | undefined {
    return this.staplePictureUrlValue;
  }
  private staplePictureUrlValue?: string;
  /** set when the staple picture could not be loaded, so the card falls back to exactly today's look */
  private staplePictureFailed = false;
  @Input() isLogMeal: boolean = false;
  @Input() isLocked: boolean = false;
  // true when the user has logged/used this food before — shows a small clock after the name
  @Input() isUsedBefore: boolean = false;
  /** the like vote to mark (1 liked, -1 disliked). Given separately from `food` so a list can
   * update the mark after a vote without replacing the food object; when not given, the card
   * reads `food.liked` */
  @Input() liked?: -1 | 1 | null;

  /** the vote the card marks: the `liked` input when given, else the food's own */
  get likedVote(): number | null | undefined {
    return this.liked !== undefined ? this.liked : this.food?.liked;
  }
  /** true when the entry has no calories and no macros at all — shows a red "No Nutrition Data"
   * pill. Never drawn together with the Check Values pill. */
  @Input() noNutritionData: boolean = false;
  /** Set when this food's stated calories cannot match its own macros: the text explaining
   * so, naming the calories the macros actually imply. Its presence is what draws the red
   * warning badge -- one input rather than a boolean beside it, so the two can never
   * disagree and draw a badge with nothing in its tooltip. The page computes it from
   * per-100 g nutrients, because the card only ever receives already-formatted ones (#4541). */
  @Input() inconsistentNutrientsInfo?: TranslatedString;
  @Input() showMenuButton: boolean = false;
  @Input() showDragHandle: boolean = false;

  // serving info inputs - food-card formats these into a display string
  @Input() servingUnit?: string;
  @Input() servingAmount?: number;
  @Input() weight?: number;
  @Input() weightUnit?: string;
  @Input() measuresLoading: boolean = false;

  // radio selection inputs
  @Input() selectable: boolean = false;
  @Input() selected: boolean = false;

  // checkbox multi-selection (shows cs-checkbox instead of radio)
  @Input() checkable: boolean = false;
  @Input() checked: boolean = false;
  @Output() checkedChange = new EventEmitter<boolean>();


  // per-card loading state (shows spinner overlay)
  @Input() loading: boolean = false;

  // match percentage (for meal suggestions)
  @Input() matchPercent?: number;

  // optional nutrients override (if not provided, uses food.displayNutrients)
  @Input() nutrients?: UiDisplayNutrient[];

  // if true, menu button appears next to title instead of in corner
  @Input() menuInTitle: boolean = false;

  // override the menu button icon (default: ellipsis-vertical)
  @Input() menuIcon: string = 'ellipsis-vertical';
  // optional label for the non-ellipsis menu button — when set, the button shows
  // its icon on small screens but the full label where there is room (>=800pt),
  // matching the scanner review rows. Unset keeps the icon-only button everywhere.
  @Input() menuLabel?: TranslatedString;

  @Output() menuClick = new EventEmitter<{ event: Event; food: FoodHeader }>();
  @Output() imageError = new EventEmitter<string>();
  @Output() selectedChange = new EventEmitter<boolean>();
  @Output() dragHandleDown = new EventEmitter<PointerEvent>();

  @HostBinding('class.selected') get isSelected() { return this.selectable && this.selected; }

  protected FoodOrigin = FoodOrigin;

  /** where this food comes from, which decides the shield, person, "Community" or "Open Food Facts" signal */
  get origin(): FoodOrigin {
    return getFoodOrigin(this.food);
  }

  constructor(elemRef: ElementRef, private settings: SettingsService, private imageService: ImageService, private cd: ChangeDetectorRef) {
    super(elemRef);
  }

  ngAfterViewInit(): void {
    this.card?.updateLayout();
  }

  ngOnDestroy(): void {
    this.destroyGuard.destroy();
  }

  override updateLayout(): void {
    this.card?.updateLayout();
  }

  override getCurrentCardHeight(): number {
    return this.card?.getCurrentCardHeight() ?? 0;
  }

  override getCardElement(): HTMLElement {
    return this.elemRef.nativeElement;
  }

  get displayNutrients(): UiDisplayNutrient[] {
    return this.nutrients || uiDisplayNutrients(this.food?.displayNutrients);
  }

  get hasImage(): boolean {
    return !!this.imageUrl;
  }

  /**
   * Whether the card shows a staple picture: only when the user has no photo of their own, the app
   * has a picture for this staple, and that picture loaded. A failed one leaves the card exactly as
   * it was before this feature — no image block at all — rather than a broken image.
   */
  get showsStaplePicture(): boolean {
    return !this.hasImage && this.staplePictureUrlValue !== undefined && !this.staplePictureFailed;
  }

  /**
   * The staple picture could not be loaded: offline on the web before the service worker has it, or
   * an app older than the upload that named it. Deliberately NOT `handleImageLoadError`, which is
   * about the user's own photo and revokes blob previews; a staple has no own photo to clear.
   */
  handleStaplePictureError(): void {
    this.staplePictureFailed = true;
    // OnPush: without this the failed <img> stays until something else redraws the card
    this.destroyGuard.safeMarkForCheck(this.cd);
  }

  // formats serving unit and weight into a display string like "1 fillet | 400 g"
  get formattedServingInfo(): string {
    if (!this.servingUnit && this.weight === undefined) {
      return '';
    }
    return formatServingSize(
      { name: this.servingUnit, amount: this.servingAmount ?? 1, weight_g: this.weight },
      this.settings.locale,
      { weightUnit: this.weightUnit },
    );
  }

  handleMenuButtonClick(event: Event) {
    event.stopPropagation();
    this.menuClick.emit({ event, food: this.food });
  }

  /** Keeps a queued (offline outbox) photo's thumbnail when its URL cannot
   * load; only a genuinely broken image reports imageError to the parent,
   * which hides the thumbnail and leaves the food's image reference alone
   * (#5692). */
  handleImageLoadError(event?: Event) {
    void (async () => {
      // if the failed src is a blob: preview, the drain just revoked it after
      // a successful upload — the reference must stay. note the card cannot
      // re-render itself here (imageUrl is a fixed @Input); the already-drawn
      // bitmap keeps showing, and the parent's next change-detection pass
      // recomputes the thumbnail url to the now-valid remote one.
      const failedImg = event?.target as HTMLImageElement | undefined;
      const failedSrc = failedImg?.currentSrc ?? '';
      if (failedSrc.startsWith('blob:')) {
        return;
      }
      // #5692: a file of the native image cache that has gone missing. the
      // cache forgets it; point this <img> at the remote copy, since imageUrl
      // is a fixed @Input and the card cannot recompute it
      const remoteUrl = this.imageService.forgetLocalImageCopy(failedSrc);
      if (remoteUrl && failedImg) {
        failedImg.src = remoteUrl;
        return;
      }
      // #3989 follow-up: while this photo's bytes are still queued in the
      // offline upload outbox, its public URL cannot load — that is not a
      // broken image. skip the error emit so the parent keeps the food's
      // image reference; the next render substitutes the outbox's local
      // preview URL (a different src), which makes the browser retry.
      if (this.imageUrl && await this.imageService.isQueuedImageUrlAsync(this.imageUrl)) {
        return;
      }
      // if the card was destroyed while awaiting the outbox lookup, the emit
      // would go to a torn-down parent binding — skip it
      if (this.destroyGuard.isDestroyed) {
        return;
      }
      this.imageError.emit(this.food?._id);
    })();
  }

  handleRadioClick(event: Event) {
    event.stopPropagation();
    this.selectedChange.emit(!this.selected);
  }
}
